import React, { useEffect, useState } from "react";
import { Form, Spin, Radio, Space, Input, Divider, Switch, Row, Col } from "antd";
import { ReactSVG } from "react-svg";

import "./General.scss";
import PrimaryButton from "../../Buttons/PrimaryButton";
import { getTranslation } from "../../../utils/helper";
import { ROUTE_PLAN_TYPIES } from "../../../constants";

const GeneralSettingsForm = ({ isFetching, setting, handleSubmit }) => {
  const [form] = Form.useForm();
  const [callDirection, setCallDirection] = useState(null);
  const [snapToRoad, setSnapToRoad] = useState(false);
  const [isGmproRoutePlanEnabled, setGmproRoutePlanEnabled] = useState(false);
  const [balance, setBalance] = useState(false);

  useEffect(() => {
    if (setting) {
      const callDirectionT = setting.hotline ? "service_hotline" : "driver_phone";
      form.setFieldsValue({
        ...setting,
        callDirection: callDirectionT,
        defaultLocationAddress: {
          address: setting.defaultLocationAddress,
          latLng: {
            lat: setting.defaultLocationLat,
            lng: setting.defaultLocationLng,
          },
        },
      });
      setSnapToRoad(setting.snapToRoad);
      setGmproRoutePlanEnabled(setting.isGmproRoutePlanEnabled);
      setBalance(setting.balance);
      setCallDirection(callDirectionT);
    }
  }, [setting]);

  const handleOnFinish = async values => {
    const params = { ...values };
    params.defaultLocationAddress = values.defaultLocationAddress?.address;
    params.defaultLocationLat = values.defaultLocationAddress?.latLng?.lat;
    params.defaultLocationLng = values.defaultLocationAddress?.latLng?.lng;
    params.driverOvertime = Number(values.driverOvertime);
    params.allowLateStop = Number(values.allowLateStop);
    params.serviceTime = Number(values.serviceTime);
    params.balanceBufferPercentage = values.balanceBufferPercentage ? Number(values.balanceBufferPercentage) : null;
    delete params.callDirection;
    if (callDirection !== "service_hotline") {
      params.hotline = null;
    }
    params.snapToRoad = snapToRoad;
    params.isGmproRoutePlanEnabled = isGmproRoutePlanEnabled;
    params.balance = balance;
    handleSubmit(params);
  };

  const onChangeCallDirection = e => {
    setCallDirection(e.target.value);
  };

  const onChangeHotline = () => {
    setCallDirection("service_hotline");
    form.setFieldsValue({
      callDirection: "service_hotline",
    });
  };

  return (
    <Spin spinning={isFetching}>
      <div className="SettingsForm GeneralSettingsForm">
        <Form form={form} scrollToFirstError layout="vertical" hideRequiredMark colon={false} onFinish={handleOnFinish}>
          <div className="FormContent">
            <Row>
              <Col span={8}>
                <Form.Item name="balance" label="Auto Balance" valuePropName="checked">
                  <Switch value={balance} onChange={checked => setBalance(checked)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                {balance && (
                  <Form.Item
                    label={getTranslation(setting, "Settings", "Buffer Percentage")}
                    name="balanceBufferPercentage">
                    <Input size="large" type="number" min={0} max={100} suffix="%" style={{ width: 240 }} />
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Divider />
            <Form.Item name="snapToRoad" label="Snap to Road" valuePropName="checked">
              <Switch onChange={checked => setSnapToRoad(checked)} />
            </Form.Item>
            <Divider />
            {setting?.isGmproEnabled && (
              <>
                <Form.Item name="isGmproRoutePlanEnabled" label="Gmpro Route Plan" valuePropName="checked">
                  <Switch onChange={checked => setGmproRoutePlanEnabled(checked)} />
                </Form.Item>
                <Divider />
              </>
            )}
            <Form.Item
              label={getTranslation(setting, "Settings", "Default Route Plan Type")}
              name="routePlanType"
              rules={[{ required: true, message: "Required" }]}>
              <Radio.Group className="RoutePlanType">
                {ROUTE_PLAN_TYPIES.map(item => (
                  <Radio key={item.value} value={item.value}>
                    {getTranslation(setting, "Route Plans", item.name)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            {/* <Form.Item
              label={getTranslation(setting, "Settings", "Optimization Mode")}
              name="optimizationMode"
            >
              <Radio.Group className="RoutePlanType">
                {OPTIMIZED_TYPES.map((item) => (
                  <Radio key={item.value} value={item.value}>
                    {getTranslation(setting, "Route Plans", item.name)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            <Divider /> */}
            <Divider />
            <Form.Item
              label={getTranslation(setting, "Settings", "Customer Call Direction")}
              name="callDirection"
              rules={[{ required: true, message: "Required" }]}>
              <Radio.Group onChange={onChangeCallDirection}>
                <Space>
                  <Radio value="service_hotline">
                    <div className="CustomerServiceHotline">
                      <span>{getTranslation(setting, "Settings", "Customer Service Hotline")}</span>
                      <Form.Item
                        name="hotline"
                        rules={[
                          {
                            required: callDirection === "service_hotline",
                            message: "Required",
                          },
                        ]}>
                        <Input size="large" style={{ width: 240 }} onChange={onChangeHotline} />
                      </Form.Item>
                    </div>
                  </Radio>
                  <Radio value="driver_phone">{getTranslation(setting, "Settings", "Driver Phone")}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            <Divider />
            <Form.Item
              label={getTranslation(setting, "Settings", "Driver Overtime")}
              name="driverOvertime"
              rules={[{ required: true, message: "Required" }]}>
              <Input
                size="large"
                type="number"
                suffix={getTranslation(setting, "Settings", "Minutes")}
                style={{ width: 240 }}
              />
            </Form.Item>
            <Divider />
            <Form.Item
              label={getTranslation(setting, "Settings", "Allow Late Stops")}
              name="allowLateStop"
              rules={[{ required: true, message: "Required" }]}>
              <Input
                size="large"
                type="number"
                suffix={getTranslation(setting, "Settings", "Minutes")}
                style={{ width: 240 }}
              />
            </Form.Item>
            <Divider />
            <Form.Item
              label={getTranslation(setting, "Settings", "Service Time")}
              name="serviceTime"
              rules={[{ required: true, message: "Required" }]}>
              <Input
                size="large"
                type="number"
                suffix={getTranslation(setting, "Settings", "Minutes")}
                style={{ width: 240 }}
              />
            </Form.Item>
            <Divider />
            <Form.Item label={getTranslation(setting, "Settings", "Token")} name="token">
              <Input.Password size="large" readOnly value={123} />
            </Form.Item>
          </div>
          <Divider />
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <PrimaryButton
                label={getTranslation(setting, "Common", "Update")}
                htmlType="submit"
                icon={<ReactSVG className="icon" src="/icons/submit.svg" />}
              />
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default GeneralSettingsForm;
